import { useEffect, useState } from "react";
import { ImagePlus, ShieldCheck, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Card, useLoad } from "./kit";
import { AiText, LevelBadge, label, levelText } from "./Ai";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { useT } from "../lib/i18n";
import { api, errMsg } from "../lib/api";
import { fmtSize, uploadFile } from "../lib/files";

const LEVEL_TONE = { "very low": "low", low: "low", medium: "medium", high: "high", "very high": "critical" };

export function InsertImage({ onInsert }) {
  const { t } = useT();
  const [pct, setPct] = useState(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPct(0);
    try {
      const f = await uploadFile(file, "image", setPct);
      onInsert(`<img src="${f.public_url}" alt="" width="560" style="max-width:100%;height:auto;border:0;">`);
      toast.success("Image uploaded and inserted");
    } catch (err) { toast.error(errMsg(err)); } finally { setPct(null); }
  };
  return (
    <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-zinc-300 transition-colors hover:bg-white/5 hover:text-white" data-testid="template-insert-image">
      <ImagePlus className="h-3.5 w-3.5" />{pct !== null ? `Uploading ${pct}%` : t("tpl.insert")}
      <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={pick} disabled={pct !== null} data-testid="template-insert-image-input" />
    </label>
  );
}

const S = {
  en: { title: "Spam check", recheck: "Re-check", checking: "Checking…", of: "/ 100 spam-likeness", risk: "Biggest risk", warn: "Mailbox filters may send this to spam. Review it before you use it.",
    tips: "How to improve", pending: "Checking your latest changes…", missed: "The automatic check didn't finish. Click Re-check to score how likely spam filters are to flag this template.",
    fix: "Rewrite to reduce spam", fixing: "Rewriting…" },
  fi: { title: "Roskapostitarkistus", recheck: "Tarkista uudelleen", checking: "Tarkistetaan…", of: "/ 100 roskapostiriski", risk: "Suurin riski", warn: "Postisuodattimet voivat ohjata tämän roskapostiin. Tarkista se ennen käyttöä.",
    tips: "Näin parannat", pending: "Tarkistetaan viimeisimpiä muutoksia…", missed: "Automaattinen tarkistus ei valmistunut. Napauta Tarkista uudelleen.",
    fix: "Kirjoita uudelleen", fixing: "Kirjoitetaan…" },
};

function useSpamPoll(tpl) {
  const [res, setRes] = useState(tpl.spam_check?.ai_classified ? tpl.spam_check : null);
  const [waiting, setWaiting] = useState(!tpl.spam_check);
  useEffect(() => {
    if (!waiting) return undefined;
    let n = 0;
    const timer = setInterval(async () => {
      n += 1;
      try {
        const { data } = await api.get(`/app/templates/${tpl.id}`);
        if (data.spam_check || n >= 20) { clearInterval(timer); setWaiting(false); if (data.spam_check?.ai_classified) setRes(data.spam_check); }
      } catch (e) { console.warn("Spam check polling stopped", e); clearInterval(timer); setWaiting(false); }
    }, 1500);
    return () => clearInterval(timer);
  }, [tpl.id, waiting]);
  return [res, setRes, waiting];
}

export function SpamCheck({ tpl, canEdit, onApplied }) {
  const { lang } = useT();
  const s = S[lang] || S.en;
  const [res, setRes, waiting] = useSpamPoll(tpl);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/app/templates/${tpl.id}/spam-check`); setRes(data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const checking = busy || (waiting && !res);
  const tips = tpl.spam_tips && (tpl.spam_tips.lang || "en") === lang ? tpl.spam_tips.text : undefined;
  return (
    <Card testId="template-spam-check">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-medium text-white"><ShieldCheck className="h-4 w-4 text-purple-300" />{s.title}</div>
        <Button size="sm" variant="ghost" onClick={run} disabled={checking} className="text-xs hover:bg-white/5 hover:text-white" data-testid="template-spam-check-run">{checking ? s.checking : s.recheck}</Button>
      </div>
      {res && <>
        <div className="flex items-end gap-3"><span className="font-display text-5xl font-semibold text-white" data-testid="template-spam-score">{res.score}</span><span className="pb-1.5 text-xs text-zinc-500">{s.of}</span>
          <span className="pb-1.5"><LevelBadge level={LEVEL_TONE[res.level]} text={res.level} testId="template-spam-level" /></span></div>
        <div className="mt-3 h-1 overflow-hidden rounded-full bg-white/5"><div className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-amber-400 to-red-500 transition-[width] duration-700" style={{ width: `${Math.max(4, res.score)}%` }} /></div>
        <p className="mt-4 text-xs text-zinc-400">{s.risk}: <span className="text-zinc-200" data-testid="template-spam-issue">{label(res.main_issue)}</span></p>
        {res.score >= 50 && <p className="mt-2 text-xs text-amber-300" data-testid="template-spam-warning">{s.warn}</p>}
        <div className="mt-5 flex flex-wrap items-start gap-2">
          {canEdit && res.score >= 30 && <RewriteButton tpl={tpl} s={s} onApplied={onApplied} />}
          <div className="w-full"><AiText key={lang} path={`/app/templates/${tpl.id}/spam-tips`} cta={s.tips} initial={tips} testId="template-spam-tips" /></div>
        </div>
      </>}
      {!res && checking && <p className="animate-pulse text-xs text-zinc-400" data-testid="template-spam-checking">{s.pending}</p>}
      {!res && !checking && <p className="text-xs text-zinc-500" data-testid="template-spam-empty">{s.missed}</p>}
    </Card>
  );
}

const R = {
  en: { title: "Suggested rewrite", desc: "Review the changes. Nothing is saved until you apply it.", before: "Current", after: "Rewritten", changes: "What changed", apply: "Apply rewrite", keep: "Keep mine", applied: "Rewrite applied and saved", subject: "Subject" },
  fi: { title: "Ehdotettu uudelleenkirjoitus", desc: "Tarkista muutokset. Mitään ei tallenneta ennen kuin hyväksyt.", before: "Nykyinen", after: "Uusi", changes: "Mitä muuttui", apply: "Käytä uutta", keep: "Pidä oma", applied: "Uusi versio tallennettu", subject: "Aihe" },
};

function RewriteButton({ tpl, s, onApplied }) {
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/app/templates/${tpl.id}/rewrite`); setDraft(data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return <>
    <Button size="sm" onClick={go} disabled={busy} data-testid="template-ai-rewrite-button"><Wand2 className={`h-3.5 w-3.5 ${busy ? "animate-pulse" : ""}`} />{busy ? s.fixing : s.fix}</Button>
    {draft && <RewriteDialog tpl={tpl} draft={draft} onClose={() => setDraft(null)} onApplied={onApplied} />}
  </>;
}

function Pane({ title, score, subject, html, text, testId }) {
  return (
    <div className="min-w-0 space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between"><span className="eyebrow">{title}</span>
        {score && <LevelBadge level={LEVEL_TONE[score.level]} text={`${score.score} · ${levelText(score.level)}`} testId={`${testId}-score`} />}</div>
      <div className="truncate rounded-lg border border-white/[0.07] bg-white/[0.02] px-3 py-2 text-xs text-zinc-200">{subject}</div>
      <iframe title={title} sandbox="" srcDoc={html || `<pre style="white-space:pre-wrap;font-family:sans-serif">${(text || "").replace(/</g, "&lt;")}</pre>`} className="h-56 w-full rounded-lg border border-white/10 bg-white sm:h-72" />
    </div>
  );
}

function RewriteDialog({ tpl, draft, onClose, onApplied }) {
  const { lang } = useT();
  const r = R[lang] || R.en;
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/app/templates/${tpl.id}`, { name: tpl.name, subject: draft.subject, html: draft.html || "", text: draft.text || "" });
      toast.success(r.applied); onClose(); onApplied?.(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-white/10 bg-[#0C0B10] sm:max-w-4xl" data-testid="template-rewrite-diff-modal">
        <DialogHeader><DialogTitle className="font-display text-xl">{r.title}</DialogTitle><DialogDescription>{r.desc}</DialogDescription></DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <Pane title={r.before} score={draft.before} subject={tpl.subject} html={tpl.html} text={tpl.text} testId="rewrite-before" />
          <Pane title={r.after} score={draft.after} subject={draft.subject} html={draft.html} text={draft.text} testId="rewrite-after" />
        </div>
        {draft.changes?.length > 0 && <div className="rounded-xl border border-purple-500/20 bg-purple-950/10 p-4" data-testid="rewrite-changes">
          <div className="eyebrow mb-2">{r.changes}</div>
          <ul className="space-y-1.5 text-sm text-zinc-300">{draft.changes.map((c) => <li key={c} className="flex gap-2"><span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-purple-300" />{c}</li>)}</ul></div>}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} className="hover:bg-white/5 hover:text-white" data-testid="rewrite-keep">{r.keep}</Button>
          <Button onClick={apply} disabled={busy} data-testid="rewrite-apply">{r.apply}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AttachmentPicker({ value, onChange }) {
  const { data } = useLoad(() => api.get("/app/files", { params: { kind: "attachment" } }).then((r) => r.data));
  if (!data?.length) return <p className="text-xs text-zinc-500">Upload attachments on the Files page to send them here.</p>;
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id].slice(0, 10));
  return (
    <div className="space-y-1" data-testid="test-send-attachments">
      <div className="text-xs text-zinc-500">Attachments</div>
      {data.slice(0, 20).map((f) => (
        <label key={f.id} className="flex cursor-pointer items-center gap-2 text-xs text-zinc-300">
          <input type="checkbox" checked={value.includes(f.id)} onChange={() => toggle(f.id)} className="accent-purple-500" data-testid={`test-send-attachment-${f.id}`} />
          <span className="truncate">{f.filename}</span><span className="text-zinc-600">{fmtSize(f.size)}</span>
        </label>))}
    </div>
  );
}
